// The browser's Back and Forward buttons move between "pages" of the app: each history
// entry carries a page id (history.state.vuexState), and the app state for that page
// is kept in IndexedDB under the same id. Changes are saved to the page the user is on,
// so going back and then changing something never overwrites a later page.
import { initializeDB, prepareStateForStorage } from "./manageIndexDb";

// Pages beyond this many are forgotten, oldest first
const MAX_PAGES = 60;

let nextNumber = 0;
const newPageId = () =>
  `${Date.now().toString(36)}-${(nextNumber++).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 6)}`;

export const currentPageId = () => history.state?.vuexState;

function readAll(db) {
  return new Promise((resolve, reject) => {
    const request = db
      .transaction(["states"], "readonly")
      .objectStore("states")
      .getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

async function prune(db) {
  const pages = await readAll(db);
  if (pages.length <= MAX_PAGES) return;
  const store = db.transaction(["states"], "readwrite").objectStore("states");
  pages
    .sort((a, b) => a.time - b.time)
    .slice(0, pages.length - MAX_PAGES)
    .forEach((page) => store.delete(page.id));
}

let saves = 0;
export async function savePage(id, state) {
  try {
    const db = await initializeDB();
    const page = { id, time: Date.now(), state: prepareStateForStorage(state) };
    await new Promise((resolve, reject) => {
      const request = db
        .transaction(["states"], "readwrite")
        .objectStore("states")
        .put(page);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    saves += 1;
    if (saves % 20 === 0) await prune(db);
  } catch (error) {
    console.error("Could not save the page", error);
  }
}

// A new page after the current one (Back returns to the current one)
export function pushPage(state) {
  const id = newPageId();
  history.pushState({ ...history.state, vuexState: id }, "");
  return savePage(id, state);
}

// Makes the current history entry a new page, without adding to the history
export function replacePage(state) {
  const id = newPageId();
  history.replaceState({ ...history.state, vuexState: id }, "");
  return savePage(id, state);
}

// Saves changes to the page the user is on
export function updatePage(state) {
  const id = currentPageId();
  if (id === undefined || id === null) return Promise.resolve();
  return savePage(id, state);
}

// The saved page with this id, or the most recently saved page for "last"
export async function readPage(id) {
  const db = await initializeDB();
  if (id === "last") {
    const pages = await readAll(db);
    return pages.reduce(
      (newest, page) => (!newest || page.time > newest.time ? page : newest),
      null
    );
  }
  return new Promise((resolve, reject) => {
    const request = db
      .transaction(["states"], "readonly")
      .objectStore("states")
      .get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

// Makes the current history entry show a saved page
export function adoptPage(id) {
  history.replaceState({ ...history.state, vuexState: id }, "");
}
